import { useCallback, useEffect, useMemo } from "react";

import { useFilterContext } from "../context/FilterContext";

export interface UseMultiSelectFilterOptions {
  /** The URL search param key (e.g. "account", "category"). */
  queryKey: string;
  /**
   * Known-valid values. IDs not in this list are filtered out and pruned
   * from the URL. Pass `undefined` to skip validation (e.g. while the
   * option list is still loading).
   */
  validValues?: string[];
  /**
   * Returns a human-readable label for a single selected value.
   * Used as the chip label override when exactly one item is selected.
   */
  getOptionLabel?: (value: string) => string | undefined;
}

export interface UseMultiSelectFilterResult {
  /** The validated selected values parsed from the URL. */
  selectedValues: string[];
  /** Replace the full selection and sync to the URL. */
  setSelectedValues: (values: string[]) => void;
  /** Chip label override — set when exactly one value is selected. */
  chipLabel: string | undefined;
  /** Chip count badge — set when more than one value is selected. */
  chipCount: number | undefined;
}

export const useMultiSelectFilter = ({
  queryKey,
  validValues,
  getOptionLabel,
}: UseMultiSelectFilterOptions): UseMultiSelectFilterResult => {
  const { filterState, setFilter, removeFilter } = useFilterContext();
  const raw = filterState[queryKey] ?? null;

  const parsedValues = useMemo(
    () => (raw ? raw.split(",").filter(Boolean) : []),
    [raw]
  );

  const validSet = useMemo(
    () => (validValues ? new Set(validValues) : undefined),
    [validValues]
  );

  const selectedValues = useMemo(() => {
    if (!validSet) return parsedValues;
    return parsedValues.filter((v) => validSet.has(v));
  }, [parsedValues, validSet]);

  // Prune invalid values from the URL.
  useEffect(() => {
    if (selectedValues.length !== parsedValues.length) {
      if (selectedValues.length) {
        setFilter(queryKey, selectedValues.join(","));
      } else {
        removeFilter(queryKey);
      }
    }
  }, [selectedValues, parsedValues, queryKey, setFilter, removeFilter]);

  const setSelectedValues = useCallback(
    (values: string[]) => {
      if (values.length) {
        setFilter(queryKey, values.join(","));
      } else {
        removeFilter(queryKey);
      }
    },
    [queryKey, setFilter, removeFilter]
  );

  const chipLabel = useMemo(() => {
    if (selectedValues.length !== 1 || !getOptionLabel) return undefined;
    return getOptionLabel(selectedValues[0]);
  }, [selectedValues, getOptionLabel]);

  const chipCount = useMemo(
    () => (selectedValues.length > 1 ? selectedValues.length : undefined),
    [selectedValues]
  );

  return { selectedValues, setSelectedValues, chipLabel, chipCount };
};
